/**
 *
 * @param method
 * @param url
 * @param body
 * @param params
 * @param signal
 * @returns
 */

import type { Nullable } from '~/core/types'

type FetchMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
type ParamsObjTypes = string | number | boolean | null | undefined
type Params = Record<string, ParamsObjTypes | ParamsObjTypes[] | Record<string, ParamsObjTypes | ParamsObjTypes[]>[]>

interface FetchFile<A, B> {
  method?: FetchMethod
  url: string
  body?: Nullable<A>
  params?: Nullable<B>
  name?: string
  visualize?: boolean
  localCallback?: () => void
}
export interface FetchResult<T> {
  data: Nullable<T>
}

/**
 *
 * @param params
 * @param parentKey
 * @returns
 */
function getStringParams(params: Params, parentKey?: string): string {
  const excludedValues: (ParamsObjTypes | Record<string, ParamsObjTypes | ParamsObjTypes[]>)[] = [undefined, null]

  const isValidValue = (value: Params[string]): boolean => {
    if (Array.isArray(value)) {
      return value.length > 0 && !value.every((i) => excludedValues.includes(i))
    }
    return !excludedValues.includes(value as undefined | null)
  }

  const buildQueryString = (value: Params[string], key: string) => {
    if (Array.isArray(value)) {
      return value.map((v, i) => typeof v === 'object' && v !== null
        ? getStringParams(v, `${key}[${i}]`)
        : `${parentKey ?? key}[${i}]=${v}`).join('&')
    }
    const baseKey = parentKey ? `${parentKey}[${key}]` : `${key}`
    return `${baseKey}=${value}`
  }

  return Object.entries(params)
    .filter(([, value]) => isValidValue(value))
    .map(([key, value]) => buildQueryString(value, key))
    .join('&')
}

async function fetchJson<T = void, B = undefined, C = Params>(
  method: FetchMethod,
  url: string,
  body?: Nullable<B>,
  params?: Nullable<C>,
  signal?: Nullable<AbortSignal>,
  credentials: RequestCredentials = 'include',
): Promise<T | null> {
  const config = useRuntimeConfig()
  let fullUrl = `${url}`
  if (params) {
    fullUrl += `?${getStringParams(params)}`
  }

  const request = new Request(fullUrl, {
    credentials,
    method,
    body: body ? JSON.stringify(body) : null,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-api-token': config.public.apiToken,
      'x-api-version': config.public.minRequiredApiVersion,
      'x-app-version': config.public.clientVersion,
      'Authorization': `Bearer ${config.public.apiToken}`,
    },
  })

  const response = await fetch(request, { signal })

  if (response.ok) {
    try {
      if (response.status === 204) {
        return null
      }
      const parsed = await response.json()
      return parsed as T
    } catch (e) {
      console.error(e)
      throw new Error(`Failed parsing server response: ${e}`)
    }
  } else {
    const error = await response.json()
    throw { ...error, status: response.status }
  }
}

/**
 *
 * @param param0
 * @returns
 */
async function fetchFile<A = undefined, B extends Params = Params>({
  method,
  url,
  params,
  body,
  name,
  visualize = false,
  localCallback,
}: FetchFile<A, B>) {
  const config = useRuntimeConfig()
  let fullUrl = `${url}`
  if (params) {
    fullUrl += `?${getStringParams(params)}`
  }
  const request = new Request(fullUrl, {
    credentials: 'include',
    method: method ?? 'GET',
    body: body ? JSON.stringify(body) : null,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-api-token': config.public.apiToken,
      'x-api-version': config.public.minRequiredApiVersion,
      'x-app-version': config.public.clientVersion,
      'Authorization': `Bearer ${config.public.apiToken}`,
    },
  })

  try {
    const response = await fetch(request)
    if (response.ok) {
      const header = response.headers.get('Content-Disposition')

      const filename = name ?? header?.match(/filename=(.+)/)?.[1] ?? 'untitled_file'

      const a = document.createElement('a')

      const blob = await response.blob()

      const objectUrl = URL.createObjectURL(blob)

      a.setAttribute('target', '_blank')
      a.setAttribute('href', objectUrl)
      a.setAttribute(visualize ? 'name' : 'download', decodeURIComponent(filename))

      a.click()
      URL.revokeObjectURL(objectUrl)
      return { data: null, meta: null }
    } else {
      const error = await response.json()

      const allowedErrors = [404, 417, 422]

      if (allowedErrors.includes(response.status)) {
        throw { ...error, status: response.status }
      } else {
        return { data: null, meta: null }
      }
    }
  } finally {
    if (localCallback) {
      localCallback()
    }
  }
}

export { fetchJson, fetchFile }
